import * as React from "react";
import { Toaster as SonnerToaster, toast as sonnerToast, type ToasterProps } from "sonner";
import { celebrate, type CelebrateIntensity } from "../lib/celebrate";

/**
 * Toaster — wrap your app once. Sonner under the hood, themed via CSS vars.
 *
 *   <Toaster /> // mount once near root
 *
 *   import { aiko } from "aisquare-pretty"
 *   aiko.success("Saved.")
 *   aiko.error("Couldn't save. Try again.")
 *   aiko.loading("Saving")  // returns id; resolve with aiko.success(id, {...})
 */
export const Toaster = (props: ToasterProps) => (
  <SonnerToaster
    position="bottom-right"
    closeButton
    richColors={false}
    toastOptions={{
      classNames: {
        toast:
          "group bg-background text-foreground border border-border rounded-2xl shadow-lg",
        title: "font-medium",
        description: "text-muted-foreground",
        actionButton: "bg-primary text-primary-foreground rounded-full",
        cancelButton: "bg-muted text-muted-foreground rounded-full",
        closeButton: "bg-muted text-muted-foreground border-border",
      },
    }}
    {...props}
  />
);

/**
 * `aiko` — voice-aligned toast helpers. Defaults written in the AIKO voice
 * (clarity over cleverness, no em-dashes). Pass your own message any time.
 */
export const aiko = {
  success: (message: React.ReactNode = "Done.", opts?: Parameters<typeof sonnerToast.success>[1]) =>
    sonnerToast.success(message, opts),
  error: (
    message: React.ReactNode = "Something broke. Try again.",
    opts?: Parameters<typeof sonnerToast.error>[1],
  ) => sonnerToast.error(message, opts),
  info: (message: React.ReactNode, opts?: Parameters<typeof sonnerToast.info>[1]) =>
    sonnerToast.info(message, opts),
  warning: (message: React.ReactNode, opts?: Parameters<typeof sonnerToast.warning>[1]) =>
    sonnerToast.warning(message, opts),
  loading: (message: React.ReactNode = "Working on it.", opts?: Parameters<typeof sonnerToast.loading>[1]) =>
    sonnerToast.loading(message, opts),
  dismiss: (id?: string | number) => sonnerToast.dismiss(id),
  /**
   * Fire confetti and show a success toast. Use for moments worth celebrating —
   * first studio shipped, milestone reached, plan upgraded. Honors
   * `prefers-reduced-motion`: skips the confetti, still shows the toast.
   *
   *   aiko.celebrate("First studio shipped.");
   *   aiko.celebrate("Milestone unlocked.", { intensity: "shower" });
   */
  celebrate: (
    message: React.ReactNode = "Nicely done.",
    opts?: Parameters<typeof sonnerToast.success>[1] & {
      intensity?: CelebrateIntensity;
    },
  ) => {
    const { intensity = "burst", ...toastOpts } = opts ?? {};
    celebrate({ intensity });
    return sonnerToast.success(message, toastOpts);
  },
  /** Escape hatch: full sonner API */
  raw: sonnerToast,
};
